'use client';
import { Dialog, DialogClose, DialogContent } from '@vs-shop/ui/dialog';
import { X } from 'lucide-react';
import { useIntl } from 'react-intl';

import LoginModalAddProductToFavouritesContent from './LoginModalAddProductToFavouritesContent';
import LoginModalContent from './LoginModalContent';
import ResetPasswordModalContent from './ResetPasswordModalContent';

import { authModalAtom, AuthModalMode } from '@vs-shop/engine/atom/ui/authModal';
import { useAtom } from '@effect/atom-react';

type Props = {
  shouldStayAtTheSamePageAfterLogin: boolean;
};

export default function AuthModal({ shouldStayAtTheSamePageAfterLogin }: Props) {
  const intl = useIntl();
  const [authState, setAuthModal] = useAtom(authModalAtom);

  const handleClose = () => {
    setAuthModal((prev) => ({ ...prev, isOpen: false }));
  };

  const dialogLabel = intl.formatMessage({
    id: 'component.authModal.dialogLabel',
    defaultMessage: 'Přihlášení',
  });

  return (
    <Dialog
      open={authState.isOpen}
      onOpenChange={(open) => {
        if (!open) {
          handleClose();
        }
      }}
    >
      <DialogContent
        data-slot="auth-modal"
        data-auth-modal-mode={authState.mode}
        aria-label={dialogLabel}
        showCloseButton={false}
        // The panel this modal has always had: the page's own ground rather
        // than the primitive's raised one, no padding of its own (each body
        // carries its own), and a measure that narrows once there is a tablet's
        // width — while keeping the primitive's gutter so it never touches the
        // viewport edge.
        className="max-h-[90vh] max-w-[min(28rem,calc(100%-2rem))] gap-0 overflow-y-auto bg-background p-0 shadow-xl tab:max-w-[min(24rem,calc(100%-2rem))] tab:p-0"
      >
        <DialogClose
          data-slot="auth-modal-close"
          className="absolute top-4 right-4 z-10 cursor-pointer rounded-full p-1 transition-colors hover:bg-gray-light"
          aria-label={intl.formatMessage({
            id: 'component.authModal.close',
            defaultMessage: 'Zavřít',
          })}
        >
          <X className="size-5" />
        </DialogClose>

        {authState.mode === AuthModalMode.login && (
          <LoginModalContent
            shouldStayAtTheSamePageAfterLogin={shouldStayAtTheSamePageAfterLogin}
          />
        )}
        {authState.mode === AuthModalMode.resetPassword && <ResetPasswordModalContent />}
        {authState.mode === AuthModalMode.addProductToFavourites && (
          <LoginModalAddProductToFavouritesContent />
        )}
      </DialogContent>
    </Dialog>
  );
}
